import { LitElement, html, css, unsafeCSS, property } from 'lit-element';
import { v4 as uuid } from 'uuid';

import gaugeChartStyles from './gauge-chart.scss';
import HighlightRule from '../../enums/highlight-rule';
import { ContextReplacementPlugin } from 'webpack';
import { StratusStatsCardComponentTemplateModel } from '../../models/components/StratusStatsCardComponentTemplateModel';

class CircleAnimationModel {
    public Context: any;
    public Width: number;
    public Height: number;
    public CurrentPercentage: number;
    public EndPercentage: number;
    public Color: string;
    public X: number;
    public Y: number;
    public Radius: number;
    public Animate: Function;
    public Step: number;

    constructor(canvas, currentPercentage, endPercentage, color, radius, animate) {
        this.Context = canvas.getContext("2d");
        this.Width = canvas.width;
        this.Height = canvas.height;
        this.CurrentPercentage = currentPercentage;
        this.EndPercentage = endPercentage;
        this.Color = color;
        this.X = this.Width / 2;
        this.Y = this.Height / 2;
        this.Radius = radius;
        this.Animate = animate;
        this.Step = this.EndPercentage / 30.0;
    }
}

export default class GaugeChart extends LitElement {
    private _data: StratusStatsCardComponentTemplateModel;
    private _id: string;
    private _onClick: Function;
    private _playlistClickThrough: boolean;

    constructor(data: StratusStatsCardComponentTemplateModel, playlistClickThrough: boolean, onClick: Function) {
        super();

        this._id = uuid();
        this._data = data;
        this._onClick = onClick;
        this._playlistClickThrough = playlistClickThrough;
    }

    static get styles() {
        return [
            css`${unsafeCSS(gaugeChartStyles)}`
        ]
    }

    firstUpdated() {
        let homeTeamCanvas = <any>this.shadowRoot.querySelector(`.home-team#canvas-${this._id}`);
        let awayTeamCanvas = <any>this.shadowRoot.querySelector(`.away-team#canvas-${this._id}`);

        let homeTeamAnimation = new CircleAnimationModel(
            homeTeamCanvas,
            0,
            this._data.homeTeam.percentageValue,
            this._data.homeTeam.highlightColor,
            60,
            this.animateCircle
        );

        homeTeamAnimation.Animate(homeTeamAnimation);

        let awayTeamAnimation = new CircleAnimationModel(
            awayTeamCanvas,
            0,
            this._data.awayTeam.percentageValue,
            this._data.awayTeam.highlightColor,
            60,
            this.animateCircle
        );

        awayTeamAnimation.Animate(awayTeamAnimation)
    }

    animateCircle(model: CircleAnimationModel) {
        model.Context.clearRect(0, 0, model.Width, model.Height);

        //outer path -begin
        model.Context.beginPath();

        model.Context.strokeStyle = "#EDEDED";
        model.Context.lineWidth = 2;

        model.Context.arc(model.X, model.Y, 70, 0 * Math.PI, 2 * Math.PI, false);
        model.Context.stroke();
        //outer path -end        

        model.Context.beginPath();
        model.Context.arc(model.X, model.Y, model.Radius, - (Math.PI / 2), ((Math.PI * 2) * (model.CurrentPercentage / 100)) - (Math.PI / 2), false);

        model.Context.strokeStyle = model.Color;
        model.Context.lineWidth = 8;
        model.Context.lineCap = "round";

        model.Context.stroke();

        model.CurrentPercentage += model.Step;

        if (model.CurrentPercentage <= model.EndPercentage) {
            requestAnimationFrame(function () {
                model.Animate(model);
            });
        }
    }

    render() {
        return [
            html`
                <div class="container">
                    <div class="heading">${this._data.heading}</div>                       
                    <div class="gauges-container">
                        <div class="gauge-container">
                            <canvas class="home-team" id="canvas-${this._id}" ></canvas>                    
                            
                            <span @click="${async (event) => await this._onClick(event, this._data.homeTeam.metricId, this._data.homeTeam.id, this._data.heading)}" 
                                class="${(this._playlistClickThrough ? "clickable" : "")} display-value"
                                title="${this._data.heading}">${this._data.homeTeam.displayValue}</span>

                            <span @click="${async (event) => await this._onClick(event, this._data.homeTeam.metricId, this._data.homeTeam.id, this._data.heading)}" 
                                class="${(this._playlistClickThrough ? "clickable" : "")} percentage-value"
                                title="${this._data.heading}">${this._data.homeTeam.percentageValue.toFixed(2)}%</span>
                        </div>
                        <div class="gauge-container">
                            <canvas class="away-team" id="canvas-${this._id}" ></canvas>
                            
                            <span @click="${async (event) => await this._onClick(event, this._data.awayTeam.metricId, this._data.awayTeam.id, this._data.heading)}" 
                                class="${(this._playlistClickThrough ? "clickable" : "")} display-value"
                                title="${this._data.heading}">${this._data.awayTeam.displayValue}</span>

                            <span @click="${async (event) => await this._onClick(event, this._data.awayTeam.metricId, this._data.awayTeam.id, this._data.heading)}"
                                class="${(this._playlistClickThrough ? "clickable" : "")} percentage-value"
                                title="${this._data.heading}">${this._data.awayTeam.percentageValue.toFixed(2)}%</span>
                        </div>
                    </div>
                </div>`
        ]
    }
}

window.customElements.define('gauge-chart', GaugeChart as any);